/**
 * Lead Service
 * 
 * Responsible for lead-related API operations.
 * Follows Single Responsibility Principle.
 */

import { FetchUtils } from '../utils/fetch-utils.js';

/**
 * Builds lead filter queries
 */
class LeadFilterBuilder {
  /**
   * Build filter for a single contact
   */
  static buildForContact(selectedContact) {
    const filter = {
      active: { type: 'boolean', value: true }
    };
    
    // If contact has customers, filter by customers, otherwise filter by contact ID
    if (selectedContact.customers && selectedContact.customers.length > 0) {
      filter.customers = selectedContact.customers.map(customer => customer._id);
    } else {
      filter.contacts = [selectedContact._id];
    }
    
    return filter;
  }

  /**
   * Build filter for multiple contacts (by customer IDs)
   */
  static buildForCustomers(customerIds) {
    return {
      active: { type: 'boolean', value: true },
      customers: customerIds
    };
  }

  /**
   * Build filter for multiple contacts (by contact IDs)
   */
  static buildForContactIds(contactIds) {
    return {
      active: { type: 'boolean', value: true },
      contacts: contactIds
    };
  }
}

/**
 * Categorizes contacts for optimal lead fetching
 */
class ContactCategorizer {
  static categorize(selectedContacts) {
    const contactsWithCustomers = selectedContacts.filter(contact => 
      contact.customers && contact.customers.length > 0
    );
    const contactsWithoutCustomers = selectedContacts.filter(contact => 
      !contact.customers || contact.customers.length === 0
    );
    
    return { contactsWithCustomers, contactsWithoutCustomers };
  }

  static extractCustomerIds(contacts) {
    return contacts
      .flatMap(contact => contact.customers.map(customer => customer._id))
      .filter((id, index, arr) => arr.indexOf(id) === index); // Remove duplicates
  }

  static extractContactIds(contacts) {
    return contacts.map(contact => contact._id);
  }
}

/**
 * Deduplicates leads by ID
 */
class LeadDeduplicator {
  static deduplicate(leads) {
    return leads.filter((lead, index, arr) => 
      arr.findIndex(l => l._id === lead._id) === index
    );
  }
}

/**
 * Main Lead Service
 * Follows Single Responsibility Principle and Open/Closed Principle
 */
export class LeadService {
  /**
   * Fetch leads for a single contact
   */
  static async fetchByContact(selectedContact) {
    const filter = LeadFilterBuilder.buildForContact(selectedContact);
    
    return FetchUtils.post('/api/lead/getCustomized', {
      filter, 
      fieldsToSelect: ['_id', 'value']
    });
  }
  
  /**
   * Fetch leads for multiple contacts
   * Handles both contacts with customers and contacts without customers
   */
  static async fetchByContacts(selectedContacts) {
    const allLeads = [];
    
    // Categorize contacts
    const { contactsWithCustomers, contactsWithoutCustomers } = 
      ContactCategorizer.categorize(selectedContacts);
    
    // Fetch leads for contacts with customers
    if (contactsWithCustomers.length > 0) {
      const customerLeads = await this._fetchLeadsByCustomers(contactsWithCustomers);
      if (customerLeads) {
        allLeads.push(...customerLeads);
      }
    }
    
    // Fetch leads for contacts without customers
    if (contactsWithoutCustomers.length > 0) {
      const contactLeads = await this._fetchLeadsByContactIds(contactsWithoutCustomers);
      if (contactLeads) {
        allLeads.push(...contactLeads);
      }
    }
    
    // Remove duplicate leads
    const uniqueLeads = LeadDeduplicator.deduplicate(allLeads);
    
    return { leads: uniqueLeads };
  }

  /**
   * Fetch leads by customer IDs
   */
  static async _fetchLeadsByCustomers(contactsWithCustomers) {
    const customerIds = ContactCategorizer.extractCustomerIds(contactsWithCustomers);
    const filter = LeadFilterBuilder.buildForCustomers(customerIds);
    
    try {
      const result = await FetchUtils.post('/api/lead/getCustomized', {
        filter, 
        fieldsToSelect: ['_id', 'value']
      });
      
      return result?.leads || [];
    } catch (error) {
      console.error('Failed to fetch leads by customers:', error);
      return [];
    }
  }

  /**
   * Fetch leads by contact IDs
   */
  static async _fetchLeadsByContactIds(contactsWithoutCustomers) {
    const contactIds = ContactCategorizer.extractContactIds(contactsWithoutCustomers);
    const filter = LeadFilterBuilder.buildForContactIds(contactIds);
    
    try {
      const result = await FetchUtils.post('/api/lead/getCustomized', {
        filter, 
        fieldsToSelect: ['_id', 'value']
      });
      
      return result?.leads || [];
    } catch (error) {
      console.error('Failed to fetch leads by contacts:', error);
      return [];
    }
  }
}

